import React from 'react';

import { creativesServiceConfigured } from '../creative-api';
import {
  getTenantSourceState,
  publishTenantSource,
  revertTenantSource,
  saveTenantSourceDraft,
  type TenantSourceKind,
  type TenantSourcePublished,
  type TenantSourceResult,
  type TenantSourceReverted,
} from '../creatives-tenant-source';
import {
  afterPublish,
  afterRevert,
  baselineOrder,
  effectiveOrder,
  emptySnapshot,
  isDirty,
  snapshotFromState,
  withDraft,
  type OrderedSourceSnapshot,
} from '../ordered-source';

/** Where a load of one ordered source has got to. `unavailable` is a route not deployed here (404);
 *  `failed` is any other GET error; `idle` is the disabled state (no source or no service). */
export type OrderedSourceStatus = 'idle' | 'loading' | 'ready' | 'unavailable' | 'failed';

export interface UseOrderedSourceInput {
  kind: TenantSourceKind;
  /** The underlying source's id (a carousel name or a game-list source id); absent disables the hook. */
  sourceId?: string;
  /** The brand's casino instance the override is keyed by; absent disables the hook. */
  casinoInstanceId?: string;
  /** How long after the last draft edit the autosave PUT fires (default 700ms). */
  debounceMs?: number;
}

export interface OrderedSource extends OrderedSourceSnapshot {
  status: OrderedSourceStatus;
  /** The order to show (draft, else baseline). */
  order: string[];
  /** The order edits are measured against (published, else live). */
  baseline: string[];
  dirty: boolean;
  /** Snapshot a new draft order now; PUT it after the debounce. The timer closure captures THIS key's
   *  (casinoInstanceId, sourceId), so a brand switch flushes the edit under the brand it was made on. */
  setDraft: (ids: string[]) => void;
  /** Send a pending draft PUT immediately (used on key change / unmount). */
  flushDraft: () => void;
  /** Publish the shown order (or `ids`) to the live site; cancels the pending autosave (the publish
   *  carries the draft) and applies the result on success. Not gated on `status`: a refused/failed load
   *  must not block the write path. */
  publish: (ids?: string[]) => Promise<TenantSourceResult<TenantSourcePublished>>;
  /** Drop the brand's own order, restoring the previously published one; cancels the pending autosave. */
  revert: () => Promise<TenantSourceResult<TenantSourceReverted>>;
  /** Re-read the source's state from the backend. */
  reload: () => void;
}

/** A queued draft write, captured at `setDraft` time so `flushDraft` sends it under the SAME
 *  (casinoInstanceId, kind, sourceId) it was made on, never the current key's. */
interface PendingDraftWrite {
  casinoInstanceId: string;
  kind: TenantSourceKind;
  sourceId: string;
  ids: string[];
}

/**
 * Owns load / debounced draft autosave / publish / revert for ONE ordered tenant-source override,
 * copy-free (the pure transitions live in `../ordered-source`) and race-safe on key change: a switch of
 * (kind, sourceId, casinoInstanceId) flushes the previous key's pending draft PUT under the previous
 * brand, aborts the previous GET, resets to an empty loading state, and re-reads; an async result is
 * applied only while it is still the current key. See the unified-tenant-source plan.
 */
export const useOrderedSource = (input: UseOrderedSourceInput): OrderedSource => {
  const { kind, sourceId, casinoInstanceId, debounceMs = 700 } = input;
  const enabled = Boolean(sourceId && casinoInstanceId) && creativesServiceConfigured();
  const key = `${kind}|${sourceId ?? ''}|${casinoInstanceId ?? ''}`;

  const [snapshot, setSnapshot] = React.useState<OrderedSourceSnapshot>(emptySnapshot);
  const [status, setStatus] = React.useState<OrderedSourceStatus>('idle');
  // The load re-runs whenever this ticks (an explicit reload).
  const [reloadTick, setReloadTick] = React.useState(0);

  // The current key, read from async resolutions to tell whether they still apply.
  const keyRef = React.useRef(key);
  // The latest snapshot, read from the write handlers without making them depend on it.
  const snapshotRef = React.useRef(snapshot);
  React.useLayoutEffect(() => {
    snapshotRef.current = snapshot;
  }, [snapshot]);

  // The pending debounced draft write and its timer.
  const pendingRef = React.useRef<PendingDraftWrite | null>(null);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Send whatever draft write is pending right now, immediately. Never consults `keyRef`: it must write
  // what was queued under the brand it was queued on, even after the key has moved on.
  const flushDraft = React.useCallback((): void => {
    if (timerRef.current !== undefined) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
    const write = pendingRef.current;
    if (!write) return;
    pendingRef.current = null;
    void saveTenantSourceDraft(write.casinoInstanceId, { kind: write.kind, source_id: write.sourceId, ids: write.ids });
  }, []);

  // Drop any pending draft write without sending it (a publish/revert carries the draft instead).
  const cancelPending = React.useCallback((): void => {
    if (timerRef.current !== undefined) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
    pendingRef.current = null;
  }, []);

  const setDraft = React.useCallback(
    (ids: string[]): void => {
      // Guard in the handler (not only via a disabled control): no source/service, no write.
      if (!casinoInstanceId || !sourceId || !creativesServiceConfigured()) return;
      setSnapshot((prev) => withDraft(prev, ids));
      // Capture THIS key's identifiers so a later flush (on brand switch) writes under this brand.
      const write: PendingDraftWrite = { casinoInstanceId, kind, sourceId, ids };
      if (timerRef.current !== undefined) clearTimeout(timerRef.current);
      pendingRef.current = write;
      timerRef.current = setTimeout(() => {
        timerRef.current = undefined;
        if (pendingRef.current !== write) return;
        pendingRef.current = null;
        void saveTenantSourceDraft(write.casinoInstanceId, {
          kind: write.kind,
          source_id: write.sourceId,
          ids: write.ids,
        });
      }, debounceMs);
    },
    [casinoInstanceId, sourceId, kind, debounceMs],
  );

  const publish = React.useCallback(
    async (ids?: string[]): Promise<TenantSourceResult<TenantSourcePublished>> => {
      if (!casinoInstanceId || !sourceId || !creativesServiceConfigured()) {
        return { ok: false, reason: 'unavailable' };
      }
      // Read the draft the publish carries BEFORE cancelling the pending write (which nulls it): an
      // explicit `ids`, else a not-yet-flushed edit, else the shown order.
      const order = ids ?? pendingRef.current?.ids ?? effectiveOrder(snapshotRef.current);
      cancelPending();
      const res = await publishTenantSource(casinoInstanceId, { kind, source_id: sourceId, ids: order });
      if (res.ok && keyRef.current === key) {
        setSnapshot((prev) => afterPublish(prev, res.value, kind === 'game_list'));
        setStatus('ready');
      }
      return res;
    },
    [casinoInstanceId, sourceId, kind, key, cancelPending],
  );

  const revert = React.useCallback(async (): Promise<TenantSourceResult<TenantSourceReverted>> => {
    if (!casinoInstanceId || !sourceId || !creativesServiceConfigured()) {
      return { ok: false, reason: 'unavailable' };
    }
    cancelPending();
    const res = await revertTenantSource(casinoInstanceId, { kind, source_id: sourceId });
    if (res.ok && keyRef.current === key) {
      setSnapshot((prev) => afterRevert(prev, res.value, kind === 'game_list'));
      setStatus('ready');
    }
    return res;
  }, [casinoInstanceId, sourceId, kind, key, cancelPending]);

  const reload = React.useCallback((): void => {
    setReloadTick((tick) => tick + 1);
  }, []);

  // Load one ordered source, re-keyed on (kind, sourceId, casinoInstanceId) and on an explicit reload.
  // On every (re)entry: flush the previous key's pending draft PUT under the previous brand (the effect
  // cleanup, which captured the old identifiers via `pendingRef`), abort the previous GET, reset to an
  // empty loading state, then GET. A reload flushes then GETs back-to-back on the SAME key, so a very
  // recent unflushed edit could be overwritten by the re-read; acceptable for an explicit refresh.
  React.useEffect(() => {
    keyRef.current = key;
    if (!enabled || !sourceId || !casinoInstanceId) {
      setSnapshot(emptySnapshot());
      setStatus('idle');
      return undefined;
    }
    setSnapshot(emptySnapshot());
    setStatus('loading');
    const controller = new AbortController();
    void getTenantSourceState(casinoInstanceId, { kind, source_id: sourceId }, controller.signal).then((res) => {
      // Apply only while this is still the current, un-aborted request (cleanup aborts on key change AND
      // unmount, so this one guard covers both).
      if (controller.signal.aborted || keyRef.current !== key) return;
      if (res.ok) {
        setSnapshot(snapshotFromState(res.value));
        setStatus('ready');
      } else if (res.reason === 'unavailable') {
        setStatus('unavailable');
      } else {
        setStatus('failed');
      }
    });
    return () => {
      flushDraft();
      controller.abort();
    };
  }, [enabled, key, kind, sourceId, casinoInstanceId, reloadTick, flushDraft]);

  return {
    ...snapshot,
    status,
    order: effectiveOrder(snapshot),
    baseline: baselineOrder(snapshot),
    dirty: isDirty(snapshot),
    setDraft,
    flushDraft,
    publish,
    revert,
    reload,
  };
};

export default useOrderedSource;
